import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CircularProgress, Box, Typography, Button, Paper, TextField, Alert } from '@mui/material';
import RedirectNavBar from '../components/RedirectNavBar';
import NotificationBanner from '../components/NotificationBanner';
import config from '../config';

export default function RedirectPage() {
    const { short_code } = useParams();
    const [link, setLink] = useState(null);
    const [linkInfo, setLinkInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [countdown, setCountdown] = useState(10);
    const [showPassword, setShowPassword] = useState(false);
    const [password, setPassword] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [pwError, setPwError] = useState('');

    // Lấy info link (và xác định có cần password không)
    useEffect(() => {
        fetch(`${config.API_BASE_URL}/api/links/public/${short_code}`)
            .then(res => {
                if (res.headers.get("content-type")?.includes("application/json")) return res.json();
                throw new Error("Invalid response");
            })
            .then(data => {
                if (data.has_password) {
                    setShowPassword(true);
                    setLinkInfo(data);
                } else {
                    setLink(data.original_url);
                    setLinkInfo(data);
                }
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [short_code]);

    // Countdown & redirect nếu đã unlock link
    useEffect(() => {
        if (!loading && link) {
            if (countdown === 0) {
                window.location.href = link;
            }
            const timer = setInterval(() => {
                setCountdown(c => c > 0 ? c - 1 : 0);
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [loading, link, countdown]);

    // Xử lý submit password
    const handleVerify = async (e) => {
        e.preventDefault();
        setVerifying(true);
        setPwError('');
        try {
            const res = await fetch(`${config.API_BASE_URL}/api/links/public/${short_code}/verify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ password })
            });
            if (res.ok) {
                const data = await res.json();
                setLink(data.original_url);
                setShowPassword(false);
            } else {
                const data = await res.json();
                setPwError(data.error || "Mật khẩu không đúng!");
            }
        } catch {
            setPwError("Lỗi kết nối server!");
        } finally {
            setVerifying(false);
        }
    };

    // Progress bar gradient
    function GradientProgress({ value = 0 }) {
        return (
            <Box sx={{
                width: "100%", mt: 2, mb: 2, px: 1
            }}>
                <Box sx={{
                    height: 10, borderRadius: 99,
                    background: "linear-gradient(90deg,#f1f5f9 0%,#e0e7ef 100%)",
                    overflow: "hidden", position: "relative"
                }}>
                    <Box sx={{
                        position: "absolute", left: 0, top: 0, bottom: 0,
                        width: `${value}%`,
                        background: "linear-gradient(90deg,#38bdf8,#6366f1,#a78bfa,#facc15)",
                        borderRadius: 99,
                        transition: "width 1s cubic-bezier(.7,.2,.4,1)"
                    }} />
                </Box>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg,#6366f1 0%,#06b6d4 100%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                pt: 10
            }}>
                <CircularProgress size={60} color="primary" />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg,#6366f1 0%,#06b6d4 100%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                px: 2
            }}
        >
            <Box sx={{ width: "100%", maxWidth: 1280, mt: { xs: 2, md: 4 } }}>
                <RedirectNavBar />
                <NotificationBanner />
            </Box>

            {!link && !showPassword ? (
                <Paper elevation={6} sx={{
                    mt: 7,
                    borderRadius: 7,
                    p: 5,
                    minWidth: 350,
                    textAlign: "center",
                    bgcolor: "#fff"
                }}>
                    <Typography variant="h5" color="error" fontWeight={800} mb={2}>
                        Link không tồn tại hoặc đã bị vô hiệu hóa!
                    </Typography>
                    <Button href="/" variant="contained" size="large" sx={{ mt: 1 }}>Về trang chủ</Button>
                </Paper>
            ) : (
                <Paper
  elevation={10}
  sx={{
    mt: { xs: 4, md: 4 }, // Đưa lên cao hơn, không quá thấp
    borderRadius: 6, // Bo nhẹ vừa phải (32px), không quá tròn
    px: { xs: 2, sm: 5, md: 10 }, // Padding ngang lớn
    py: { xs: 3, sm: 6, md: 6 },  // Padding dọc
    minWidth: { xs: "96vw", sm: 400 },
    maxWidth: 650,
    mx: "auto",
    textAlign: "center",
    background: "#fff",
    boxShadow: "0 12px 64px #6366f133, 0 2px 16px #06b6d433",
    display: "flex",
    flexDirection: "column",
    alignItems: "center"
  }}
>
  <img src="/Logo.png" width={70} alt="logo" style={{ marginBottom: 16 }} />
  <Typography variant="h3" fontWeight={900} sx={{
    color: "#6366f1", mb: 1.2, letterSpacing: 1, fontSize: { xs: 25, sm: 32 }
  }}>
    Đang chuyển hướng...
  </Typography>
  <Typography sx={{ mb: 2, color: "#333", fontSize: 19, fontWeight: 700 }}>
    {linkInfo?.link_name}
    {linkInfo?.note && (
      <Box component="span" sx={{ fontStyle: "italic", color: "#06b6d4", ml: 1 }}>
        ({linkInfo.note})
      </Box>
    )}
  </Typography>

                    {/* Form nhập mật khẩu */}
                    {showPassword && !link && (
                        <Box component="form" onSubmit={handleVerify} sx={{ mt: 1 }}>
                            <Typography color="text.secondary" sx={{ mb: 2, fontSize: 16 }}>
                                Link này được bảo vệ, vui lòng nhập mật khẩu để tiếp tục!
                            </Typography>
                            <TextField
                                type="password"
                                label="Mật khẩu"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                fullWidth
                                variant="outlined"
                                sx={{ mb: 2 }}
                                disabled={verifying}
                                autoFocus
                            />
                            {pwError && <Alert severity="error" sx={{ mb: 2 }}>{pwError}</Alert>}
                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                fullWidth
                                sx={{
                                    fontWeight: 900,
                                    borderRadius: 99,
                                    background: "linear-gradient(90deg,#06b6d4,#6366f1)",
                                    boxShadow: "0 4px 24px #38bdf855,0 2px 8px #6366f133",
                                    "&:hover": { background: "linear-gradient(90deg,#6366f1,#facc15)" }
                                }}
                                disabled={verifying}
                            >
                                {verifying ? "Đang xác thực..." : "Xác nhận"}
                            </Button>
                        </Box>
                    )}

                    {/* Nếu đã unlock (có link), hiển thị đếm ngược */}
                    {link && (
                        <>
                            <Typography color="text.secondary" sx={{ mb: 1.5, fontSize: 17 }}>
                                Bạn sẽ được chuyển tới <b>trang đích</b> sau{" "}
                                <span style={{
                                    color: "#facc15",
                                    fontWeight: 900,
                                    fontSize: 24,
                                    textShadow: "0 1px 10px #facc1540,0 0px 4px #fff"
                                }}>{countdown}</span>{" "}
                                <span style={{
                                    color: "#6366f1",
                                    fontWeight: 700,
                                    fontSize: 18
                                }}>giây</span>.
                            </Typography>
                            <GradientProgress value={100 - (countdown * 10)} />
                            <Button
                                onClick={() => window.location.href = link}
                                variant="contained"
                                size="large"
                                sx={{
                                    mt: 2,
                                    px: 5,
                                    fontWeight: 900,
                                    borderRadius: 99,
                                    fontSize: 18,
                                    background: "linear-gradient(90deg,#06b6d4,#6366f1)",
                                    boxShadow: "0 4px 24px #38bdf855,0 2px 8px #6366f133",
                                    "&:hover": {
                                        background: "linear-gradient(90deg,#6366f1,#facc15)"
                                    }
                                }}
                            >
                                CHUYỂN NGAY
                            </Button>
                        </>
                    )}

                    <Typography mt={3} variant="body2" color="textSecondary" fontSize={16}>
                        Powered by <b style={{ color: "#6366f1" }}>Visaothe Shortener</b>
                    </Typography>
                </Paper>
            )}
        </Box>
    );
}
